import { VehicleEligibilityRequirement } from './vehicle-requirements'
import { ChargerEligibilityRequirements } from './charger-requirements'
import { ThermostatEligibilityRequirements } from './thermostat-requirements'
import { BehavioralEligibilityRequirements } from './behavioral-requirements'
import { Vehicle } from '../vehicle'
import { Thermostat } from '../thermostat'
import { VehicleCharger } from '../vehicleCharger'
import { Profile } from '../user'
import { ProfileEligibilityRequirements } from './profile-requirements'
import {
  AccountFieldsConfig,
  EnrollmentVerificationStatus,
  UtilityProgramViewConfig,
  VehicleMakeModels,
} from '../utilityProgram'
import { SurveyResponse } from '../surveyResponse'
import { ProgramReward } from '../program-rewards/program-rewards'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { EnergySystem } from '../energySystem'
import { EnergySystemEligibilityRequirements } from './energy-system-requirements'
import { Utility } from '@/types/utility'
import { type UUID } from '../uuid'

export type UtilityProgram = {
  id: number
  name: string
  logo_url: string
  tos_url: string
  faq_url: string
  referral_points: string
  unenrolled_survey_url: string
  view_config_json: UtilityProgramViewConfig
  /** See {@link AccountFieldsConfig}. Optional for back-compat (undefined = legacy single account-number field). */
  account_fields_config?: AccountFieldsConfig
  utility_ids: number[]
  utility_plans: number[]
  device_eligibility: string
  external_id: string
  preferred_display_timezone: string
  eligible_vehicle_makes: string
  max_device_count: number
  required_enrollment_survey_responses: SurveyResponse[]
  utilities: Utility[]
  utilities_api_provider_urls: Record<number, string>
  reward_device_limits?: DeviceLimits
  excluded_vehicle_make_models: VehicleMakeModels[]
  active: boolean
}

export enum DeviceParticipationStatus {
  ACTIVE = 'Active',
  PENDING = 'Pending',
  INACTIVE = 'Inactive',
  COMPLETED = 'Completed',
}

export enum DeviceEligibilityStatus {
  ELIGIBLE = 'Eligible',
  INELIGIBLE = 'Ineligible',
}

type EnrollmentRoot = {
  id: number
  unenrolled_at: string
  ineligible: boolean
  utility_program_enrollment_id: number
  group?: { alias?: string; cost_category_optimization_enabled?: boolean }
  utility_program_id: string
  unenrolled_reasons: string
  unenrolled_source: string
  enrolled: boolean
  view_config_json?: UtilityProgramViewConfig
  eligibility_status?: DeviceEligibilityStatus
  participation_status?: DeviceParticipationStatus
}

type VehicleEnrollment = EnrollmentRoot & {
  vehicle_id: number
}

type ThermostatEnrollment = EnrollmentRoot & {
  thermostat_id: number
}

type ChargerEnrollment = EnrollmentRoot & {
  charger_id: number
}

type BehavioralEnrollment = EnrollmentRoot & {
  profile_id: number
}

type EnergySystemEnrollment = EnrollmentRoot & {
  energy_system_id: string
}

export const isEnrollment = (x: unknown): x is EnrollmentRoot => {
  return (x as EnrollmentRoot).enrolled !== undefined
}

export const isVehicleEnrollment = (
  x: EnrollmentRoot,
): x is VehicleEnrollment => {
  return (x as VehicleEnrollment).vehicle_id !== undefined
}

export const isChargerEnrollment = (
  x: EnrollmentRoot,
): x is ChargerEnrollment => {
  return (x as ChargerEnrollment).charger_id !== undefined
}

export const isThermostatEnrollment = (
  x: EnrollmentRoot,
): x is ThermostatEnrollment => {
  return (x as ThermostatEnrollment).thermostat_id !== undefined
}

export const isBehavioralEnrollment = (
  x: EnrollmentRoot,
): x is BehavioralEnrollment => {
  return (x as BehavioralEnrollment).profile_id !== undefined
}

export const isEnergySystemEnrollment = (
  x: EnrollmentRoot,
): x is EnergySystemEnrollment => {
  return (x as EnergySystemEnrollment).energy_system_id !== undefined
}

type EnrollmentOverview = {
  program_enrollment: {
    id: number
    unenrolled_at: string | null
    external_redirect_url: string
    verification_status: EnrollmentVerificationStatus
    verified_at: string
    enrolled: boolean
  }
  vehicle_enrollments: (VehicleEnrollment & {
    vehicle: Vehicle
    view_config_json?: UtilityProgramViewConfig
  })[]
  thermostat_enrollments: (ThermostatEnrollment & {
    thermostat: Thermostat
    view_config_json?: UtilityProgramViewConfig
  })[]
  charger_enrollments: (ChargerEnrollment & {
    charger: VehicleCharger
    view_config_json?: UtilityProgramViewConfig
  })[]
  behavioral_enrollments: (BehavioralEnrollment & {
    view_config_json?: UtilityProgramViewConfig
  })[]
  energy_system_enrollments: (EnergySystemEnrollment & {
    energy_system: EnergySystem
    view_config_json?: UtilityProgramViewConfig
  })[]
}

type RequirementResultRoot = {
  name: string
  reason: string
  is_eligible: boolean
  ineligible_reasons: string[]
  optional: boolean
}

type VehicleRequirementResult = RequirementResultRoot & {
  key: VehicleEligibilityRequirement
}

type ChargerRequirementResult = RequirementResultRoot & {
  key: ChargerEligibilityRequirements
}

type EnergySystemRequirementResult = RequirementResultRoot & {
  key: EnergySystemEligibilityRequirements
}

type ThermostatRequirementResult = RequirementResultRoot & {
  key: ThermostatEligibilityRequirements
}

type BehavioralRequirementResult = RequirementResultRoot & {
  key: BehavioralEligibilityRequirements
}

export type ProfileRequirementsResult = RequirementResultRoot & {
  key: ProfileEligibilityRequirements
  surveyType?: string
}

type VehicleRequirementResults = {
  requirements_result: VehicleRequirementResult[]
}
type ChargerRequirementResults = {
  requirements_result: ChargerRequirementResult[]
}
type ThermostatRequirementResults = {
  requirements_result: ThermostatRequirementResult[]
}
type BehavioralRequirementResults = {
  requirements_result: BehavioralRequirementResult[]
}
type EnergySystemRequirementResults = {
  requirements_result: EnergySystemRequirementResult[]
}

type EligibilityRoot = {
  name: string
  is_eligible: boolean
}

type VehicleEligibilityRoot = EligibilityRoot &
  VehicleRequirementResults &
  ({ id: number; vehicle: Vehicle } | { id: null; vehicle: null })

type ChargerEligibilityRoot = EligibilityRoot &
  ChargerRequirementResults &
  ({ id: number; charger: VehicleCharger } | { id: null; charger: null })

type ThermostatEligibilityRoot = EligibilityRoot &
  ThermostatRequirementResults &
  ({ id: number; thermostat: Thermostat } | { id: null; thermostat: null })

type BehavioralEligibilityRoot = EligibilityRoot &
  BehavioralRequirementResults & { id: number | null }

type ProfileEligibilityRoot = EligibilityRoot & {
  profile: Profile
  requirements_result: ProfileRequirementsResult[]
}

type EnergySystemEligibilityRoot =
  | EligibilityRoot &
      EnergySystemRequirementResult &
      (
        | { id: UUID; energy_system: EnergySystem }
        | { id: null; energy_system: null }
      )

export type DeviceEligibilityOverview = {
  profile: ProfileEligibilityRoot
  devices: {
    vehicles: VehicleEligibilityRoot[] | null
    chargers: ChargerEligibilityRoot[] | null
    thermostats: ThermostatEligibilityRoot[] | null
    behavioral: BehavioralEligibilityRoot[] | null
    energy_systems: EnergySystemEligibilityRoot[] | null
  }
}

export type DeviceLimit = {
  min: number | null
  max: number | null
}

export type DeviceLimits = {
  vehicle: DeviceLimit | null
  thermostat: DeviceLimit | null
  charger: DeviceLimit | null
  behavioral: DeviceLimit | null
  energy_system: DeviceLimit | null
  total_devices: DeviceLimit | null
}

type ProgramsNew = {
  utility_program: UtilityProgram
  enrollment_overview: EnrollmentOverview | null
  device_eligibility_overview: DeviceEligibilityOverview
  device_limits: DeviceLimits
  rewards: ProgramReward[]
}

type APIProgramRewardsType =
  | 'flat_rate'
  | 'charge_incentive'
  | 'sign_up'
  | 'referral'
  | 'end_of_program'

type ProgramRewardsDeviceAmount = {
  deviceType: DeviceType
  dollarAmount?: number | null
  dollarAmountYearly?: number | null
}
type APIProgramRewardsByCategory = {
  enrollment?: ProgramRewardsDeviceAmount[]
  referral?: ProgramRewardsDeviceAmount[]
  participation?: ProgramRewardsDeviceAmount[]
  offPeakCharging?: ProgramRewardsDeviceAmount[]
  endOfProgram?: ProgramRewardsDeviceAmount[]
}

type ProgramRewardsByCategory = APIProgramRewardsByCategory & {
  application?: ProgramRewardsDeviceAmount[]
}

type DynamicYearlyProgramRewards = Omit<
  ProgramRewardsByCategory,
  'offPeakCharging' | 'referral'
>
type ProgramRewardsType =
  | 'application'
  | 'enrollment'
  | 'referral'
  | 'participation'
  | 'offPeakCharging'
  | 'endOfProgram'

const APIProgramRewardsMapping: Record<
  APIProgramRewardsType,
  ProgramRewardsType
> = {
  flat_rate: 'participation',
  charge_incentive: 'offPeakCharging',
  sign_up: 'enrollment',
  referral: 'referral',
  end_of_program: 'endOfProgram',
}

export type {
  ProgramsNew,
  EnrollmentOverview,
  EnrollmentRoot,
  VehicleEnrollment,
  BehavioralEnrollment,
  ChargerEnrollment,
  ThermostatEnrollment,
  ProgramRewardsType,
  APIProgramRewardsType,
  ProgramRewardsDeviceAmount,
  APIProgramRewardsByCategory,
  ProgramRewardsByCategory,
  DynamicYearlyProgramRewards,
  EnergySystemEnrollment,
  VehicleRequirementResult,
  ThermostatRequirementResult,
  ChargerRequirementResult,
  BehavioralRequirementResult,
  EnergySystemRequirementResult,
  VehicleEligibilityRoot,
  ThermostatEligibilityRoot,
  ChargerEligibilityRoot,
  BehavioralEligibilityRoot,
  ProfileEligibilityRoot,
  EnergySystemEligibilityRoot,
  VehicleEligibilityRequirement,
  ChargerEligibilityRequirements,
  ThermostatEligibilityRequirements,
  BehavioralEligibilityRequirements,
}

export { APIProgramRewardsMapping }
